import { Alert, AutoComplete, Input, Modal, Select, Typography } from "antd";
import { REVIEW_NOTE_MAX_LENGTH, REVIEW_REASON_CODES, overrideChoices, overrideValue, reviewReasonCodeLabel } from "common/reviewQueue";
import React from "react";

interface IReviewItemActionModalProps {
    // "merge" or "dismiss"; the modal is closed when null.
    action: string | null;
    item: IReviewQueueItem;
    // Member records by dataObjectId: the record (PII masked), null when gone, or { readError }.
    records: { [dataObjectId: string]: any };
    fields: { fieldId: string; name?: string; isPII?: boolean; dataType?: string }[];
    acting: boolean;
    // Why the last attempt failed, shown until the steward tries again.
    errorMessage?: string | null;
    onSubmit: (request: IReviewItemActionRequest) => void;
    onCancel: () => void;
}

const TEXT: { [action: string]: { title: string; ok: string; help: string } } = {
    // A steward conflict was already merged: the steward settles the values it kept.
    resolve: {
        title: "Resolve the conflict",
        ok: "Resolve",
        help: "These records were already merged. Pick or type the value to keep for each field to check, or leave the value the merge kept.",
    },
    merge: {
        title: "Merge these records",
        ok: "Merge",
        help: "The records are merged into the survivor now. While Golden Nominal maintenance is on the duplicates are kept, and a deduplication run's options decide whether they are retired. For each field to check, pick or type the value to keep, or leave the value the merge chooses.",
    },
    birth: {
        title: "Create a golden record",
        ok: "Create",
        help: "A golden record is created now from these records, in the schema's golden dataset; the records themselves stay as they are, linked to it. For each field to check, pick or type the value to keep, or leave the value the merge chooses.",
    },
    subsume: {
        title: "Flag as subsumed",
        ok: "Flag as subsumed",
        help: "The duplicate is flagged as subsumed by its survivor: it stays in its dataset, is left out of later duplicate searches, and can be retired from Subsumed records. Values only it holds are not copied to the survivor.",
    },
    dismiss: {
        title: "Dismiss this item",
        ok: "Dismiss",
        help: "Nothing is merged or changed. The item is closed, and raised again only if one of its records changes.",
    },
};

// Asks a steward why they merge or dismiss a review item, and, for a merge, which value to keep
// for each field the item asks them to check: one of the records' values, or one they type.
const ReviewItemActionModal = ({ action, item, records, fields, acting, errorMessage, onSubmit, onCancel }: IReviewItemActionModalProps) => {
    const [reasonCode, setReasonCode] = React.useState<string | undefined>();
    const [note, setNote] = React.useState("");
    // What the steward picked or typed, by fieldId; a field left empty keeps the merge's value.
    const [entries, setEntries] = React.useState<{ [fieldId: string]: string }>({});

    // A fresh form each time the modal opens for an item (not when the same item is read again).
    React.useEffect(() => {
        setReasonCode(undefined);
        setNote("");
        setEntries({});
    }, [action, item.itemId]);

    if (!action) {
        return null;
    }
    const conflict = item.type === "STEWARD_CONFLICT";
    const text = TEXT[action === "merge" && conflict ? "resolve" : action];
    const choices = action === "merge" || action === "birth" ? overrideChoices(fields, item, records) : [];
    const parsed = choices
        .filter((choice) => entries[choice.fieldId] !== undefined && entries[choice.fieldId] !== "")
        .map((choice) => ({ fieldId: choice.fieldId, ...overrideValue(choice.dataType, entries[choice.fieldId]) }));
    const invalid = parsed.filter((entry) => "error" in entry);
    // A conflict's values are written only onto the survivor as the steward saw it.
    const survivor = item.proposedSurvivorId ? records[item.proposedSurvivorId] : null;
    const survivorVersion = survivor && !survivor.readError && Number.isInteger(survivor.version) ? survivor.version : null;
    const needsSurvivorVersion = conflict && parsed.length > 0;
    const noteNeeded = reasonCode === "OTHER" && !note.trim();
    const canSubmit = !!reasonCode && !noteNeeded && invalid.length === 0 && typeof item.rev === "number" && !(needsSurvivorVersion && survivorVersion === null);

    const submit = () => {
        if (!canSubmit) {
            return;
        }
        const request: IReviewItemActionRequest = { rev: item.rev as number, reasonCode: reasonCode as string };
        if (note.trim()) {
            request.note = note.trim();
        }
        if (parsed.length > 0) {
            request.overrides = Object.fromEntries(parsed.map((entry) => [entry.fieldId, (entry as { value: string | number | boolean }).value]));
            if (needsSurvivorVersion) {
                request.survivorVersion = survivorVersion as number;
            }
        }
        onSubmit(request);
    };

    return (
        <Modal
            open
            title={text.title}
            okText={text.ok}
            okButtonProps={{ disabled: !canSubmit, danger: action === "merge", "data-testid": "review-action-submit-button" } as any}
            confirmLoading={acting}
            onOk={submit}
            onCancel={onCancel}
            destroyOnHidden
            data-testid="review-action-modal"
        >
            <div className="flex flex-col gap-3">
                <Typography.Paragraph type="secondary" className="!mb-0">
                    {text.help}
                </Typography.Paragraph>
                {errorMessage && <Alert type="error" showIcon title={errorMessage} data-testid="review-action-error" />}
                {choices.map((choice) => {
                    const error = invalid.find((entry) => entry.fieldId === choice.fieldId);
                    return (
                        <div key={choice.fieldId}>
                            <Typography.Text strong>{choice.name}</Typography.Text>
                            <AutoComplete
                                allowClear
                                className="w-full"
                                placeholder="Keep the value the merge chose"
                                value={entries[choice.fieldId]}
                                options={choice.options.map((value) => ({ label: String(value), value: String(value) }))}
                                onChange={(value) => setEntries({ ...entries, [choice.fieldId]: value ?? "" })}
                                status={error ? "error" : undefined}
                                data-testid={`review-action-override-${choice.fieldId}`}
                            />
                            {error && "error" in error && <Typography.Text type="danger">{error.error}</Typography.Text>}
                        </div>
                    );
                })}
                {needsSurvivorVersion && survivorVersion === null && (
                    <Alert type="warning" showIcon title="The surviving record could not be read, so values cannot be written to it. Reload the item." />
                )}
                <div>
                    <Typography.Text strong>Reason</Typography.Text>
                    <Select
                        className="w-full"
                        placeholder="Why?"
                        value={reasonCode}
                        options={REVIEW_REASON_CODES[action as keyof typeof REVIEW_REASON_CODES].map((code) => ({ label: reviewReasonCodeLabel(code), value: code }))}
                        onChange={setReasonCode}
                        data-testid="review-action-reason"
                    />
                </div>
                <div>
                    <Typography.Text strong>Note{reasonCode === "OTHER" ? "" : " (optional)"}</Typography.Text>
                    <Input.TextArea
                        rows={3}
                        maxLength={REVIEW_NOTE_MAX_LENGTH}
                        showCount
                        value={note}
                        onChange={(event) => setNote(event.target.value)}
                        status={noteNeeded ? "error" : undefined}
                        data-testid="review-action-note"
                    />
                    {noteNeeded && <Typography.Text type="danger">Say why, when the reason is Other.</Typography.Text>}
                </div>
            </div>
        </Modal>
    );
};

export default ReviewItemActionModal;
